import { Component, Prop, Watch } from "@stencil/core";
import io, { Socket } from "socket.io-client";
import dayjs from "dayjs";
import { keys, isEmpty, values, isNil } from "lodash-es";
import {
  ILiftStatusSensorResponse,
  ILiftStatusSensorDataResponse,
  ILiftStatusModuleStatus,
  TLiftStatusWebComponentErrorType,
  ILiftStatusValues,
  ILiftStatusUpdated,
} from "../../utils/types";
import {
  authenticate,
  fetchSensorData,
  fetchSensorSummary,
} from "../../utils/api";
import {
  createNewStore,
  getStore,
  ILiftStatusStore,
  removeStore,
  updateStore,
} from "../../store";
import { ErrorMessageKey, getLocale } from "../../utils/i18n";
import { getErrorMessageForDisplay } from "../../utils/error-message";
import {
  DATA_REQUEST_SENSOR_IDS,
  HealthStatus,
  SENSOR,
  getHealthStatus,
} from "../../utils/sensors";
import {
  ErrorType,
  ObjectType,
  TOKEN_OTYPE_TO_OBJECT_TYPE,
} from "../../utils/status";

@Component({
  tag: "ls-data",
  shadow: true,
})
export class LsData {
  /**
   * The token used to connect to the Socket.IO and API servers.
   */
  @Prop() readonly accessToken: string;

  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  /**
   * Extra sensor IDs that should be included in the API requests.
   */
  @Prop() readonly sensorIds: string;

  /**
   * The URL for the API endpoint.
   */
  @Prop() readonly apiUrl = "https://api-gcp-prod.liftstatus.nl";

  /**
   * Global locale for all components in this installation (`nl` or `en`).
   */
  @Prop() readonly language?: string;

  @Watch("language")
  syncStoreLocaleFromLanguage(newLanguage?: string) {
    if (this.store === null) return;
    this.store.set("locale", getLocale(newLanguage));
  }

  private store: ILiftStatusStore | null = null;

  /**
   * The computed idKey used for the store. Cached to allow cleanup in disconnectedCallback.
   */
  private computedIdKey: string | null = null;

  /**
   * The list of relevant sensors that is used when performing API requests.
   */
  private readonly availableSensorIds = DATA_REQUEST_SENSOR_IDS;

  /**
   * The ID of the connection with the socket server.
   * Currently not in use. Can be used in the future to distinguish between multiple installations.
   */
  private socketId: string;

  /**
   * Reference of the connection with the socket server.
   */
  private socket: Socket;

  async componentWillLoad() {
    // Check for a valid access token and if so decode it
    const {
      isValid,
      objectId,
      objectType,
      hasResetContactCapability,
      isAdmin,
    } = await this.decodeAccessToken(this.accessToken);
    if (!isValid) return;

    // Check if the installation has a back door
    const hasBackDoorPromise = this.checkHasBackdoor(
      objectType,
      objectId,
      this.accessToken,
    );

    const mergedSensorIds = [
      // Remove duplicates using a Set
      ...new Set(
        this.availableSensorIds.concat(
          this.sensorIds !== undefined
            ? this.sensorIds
                .split(",")
                .filter((id) => id.trim() !== "")
                .map((id) => parseInt(id.trim(), 10))
            : [],
        ),
      ),
    ];

    // Get the historical data from the past hour
    const historicalDataPastHourPromise = this.getHistoricalData(
      dayjs().subtract(1, "hour").format(),
      dayjs().format(),
      mergedSensorIds,
      objectType,
      objectId,
      this.accessToken,
    );

    // Connect to the socket server
    const connectedPromise = this.connectToSocketServer(this.accessToken);

    // We stored all the promises, so now we can fire them concurrently
    const [hasBackDoor, historicalDataPastHour, { connected, socket }] =
      await Promise.all([
        hasBackDoorPromise,
        historicalDataPastHourPromise,
        connectedPromise,
      ]);

    // If something went wrong an error should already be thrown, so simply return here
    if (isNil(hasBackDoor) || isNil(historicalDataPastHour) || !connected) {
      return;
    }

    // We always need a key to identify the store data, so if no key is provided we create one ourselves
    const idKey = this.idKey ?? `${objectType}-${objectId}`;

    // Store the computed idKey for cleanup in disconnectedCallback
    this.computedIdKey = idKey;

    const currentSensorData = this.getCurrentDataFromHistorical(
      historicalDataPastHour,
    );

    // Save state in store
    createNewStore(idKey, {
      hasBackDoor,
      historicalSensorData: historicalDataPastHour,
      currentSensorData,
      locale: getLocale(this.language),
      accessToken: this.accessToken,
      apiUrl: this.apiUrl,
      externalIdentifier: objectId,
      objectType,
      hasResetContactCapability,
      isAdmin,
    });

    try {
      this.store = await getStore(idKey);
    } catch (error) {
      this.throwError(
        "STORE_CREATION_FAILED",
        ErrorType.Token,
        error instanceof Error ? error.message : String(error),
      );
    }

    if (
      getHealthStatus(
        currentSensorData.values[SENSOR.SensecloudConnectionHealthy],
      ) === HealthStatus.Unhealthy
    ) {
      this.throwError("SENSECLOUD_CONNECTION_FAILED", ErrorType.Sensecloud);
    } else if (
      getHealthStatus(
        currentSensorData.values[SENSOR.ControllerConnectionHealthy],
      ) === HealthStatus.Unhealthy
    ) {
      this.throwError("CONTROLLER_CONNECTION_FAILED", ErrorType.Controller);
    }

    // Listen to future realtime updates
    this.listenForSensorUpdates(socket, idKey, mergedSensorIds);
  }

  /**
   * Checks for valid access token and decodes it
   * @param accessToken
   * @returns \{ isValid: true, objectId, objectType \} if the token is valid
   * @returns \{ isValid: false \}  if the token is invalid
   */
  private async decodeAccessToken(accessToken: string): Promise<{
    isValid: boolean;
    objectId?: string;
    objectType?: ObjectType;
    hasResetContactCapability?: boolean;
    isAdmin?: boolean;
  }> {
    if (isEmpty(accessToken)) {
      this.throwError(
        "NO_ACCESS_TOKEN",
        ErrorType.Token,
        "Add the token to the <ls-data> component like so: <ls-data access-token='yourToken'></ls-data>",
      );
      return {
        isValid: false,
      };
    }

    try {
      const response = await authenticate({ apiUrl: this.apiUrl, accessToken });

      const objectId = response.tokenData.oid;
      const objectType = TOKEN_OTYPE_TO_OBJECT_TYPE[response.tokenData.otype];
      const hasResetContactCapability = Boolean(
        response.response?.capability?.commands?.reset_contact,
      );
      const isAdmin = response.response?.permissions?.commands === true;

      return {
        isValid: true,
        objectId,
        objectType,
        hasResetContactCapability,
        isAdmin,
      };
    } catch (error) {
      this.throwError(
        "INVALID_ACCESS_TOKEN",
        ErrorType.Token,
        error instanceof Error ? error.message : String(error),
      );
      return {
        isValid: false,
      };
    }
  }

  /**
   * This function checks if the elevator has a back door.
   * It does this by checking for activity on the rear door movement counter
   * (SENSOR.DoorMovementCounterRear) for the past week.
   */
  private async checkHasBackdoor(
    objectType: ObjectType,
    objectId: string,
    accessToken: string,
  ) {
    const fromTS = dayjs().subtract(7, "day").format();

    try {
      const response = await fetchSensorSummary({
        apiUrl: this.apiUrl,
        accessToken,
        objectType,
        objectId,
        sensorId: SENSOR.DoorMovementCounterRear,
        from: fromTS,
      });

      // Without any rows there is definitely no back door
      if (response.rows.length === 0) return false;

      const start = response.rows[0]?.start;
      const end = response.rows[0]?.end;

      return end - start === 0 ? false : true;
    } catch (error) {
      this.throwError(
        "BACK_DOOR_CHECK_FAILED",
        ErrorType.API,
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  /**
   * This function retrieves historical data from the API server for a specified amount of time.
   * @param {string} fromTS A formatted Day.js object.
   * @param {string} toTS A formatted Day.js object.
   */
  private async getHistoricalData(
    fromTS: string,
    toTS: string,
    sensorList: number[],
    objectType: ObjectType,
    objectId: string,
    accessToken: string,
  ) {
    try {
      const response = await fetchSensorData({
        apiUrl: this.apiUrl,
        accessToken,
        objectType,
        objectId,
        from: fromTS,
        to: toTS,
        sensors: sensorList,
      });

      return response;
    } catch (error) {
      this.throwError(
        "HISTORIC_DATA_FAILED",
        ErrorType.API,
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  private connectToSocketServer(
    accessToken: string,
  ): Promise<{ connected: boolean; socket?: Socket }> {
    // Close any existing connections
    if (!isEmpty(this.socketId)) {
      this.closeSocketConnection();
    }

    return new Promise((resolve) => {
      this.socket = io(this.apiUrl, {
        transports: ["websocket"],
        path: "/liftstatus/socket.io",
        reconnectionAttempts: 5,
        query: {
          access_token: `${accessToken}`,
        },
      });

      // Wait until the io function returns a connection
      this.socket.on("connect", () => {
        resolve({
          connected: true,
          socket: this.socket,
        });
      });

      // Listen for any errors
      this.socket.on("error", (error: string) => {
        this.throwError(
          "SOCKET_CONNECTION_FAILED",
          ErrorType.Socket,
          error,
          this.socket,
        );
        resolve({ connected: false });
      });
    });
  }

  private listenForSensorUpdates(
    socket: Socket,
    idKey: string,
    sensorIds: number[],
  ) {
    // Responds with live updates from the socket server
    socket.on("module", ({ message }: ILiftStatusModuleStatus) => {
      // Update data values and timestamp
      const data = message.data;
      const dataKey = keys(data)[0];
      const dataSensorId = parseInt(dataKey, 10);
      const dataValue = values(data)[0];
      const timestamp = message.ts;
      const dataHealth = getHealthStatus(dataValue);

      if (
        dataSensorId === SENSOR.SensecloudConnectionHealthy &&
        dataHealth === HealthStatus.Unhealthy
      ) {
        this.throwError("SENSECLOUD_CONNECTION_FAILED", ErrorType.Sensecloud);
      } else if (
        dataSensorId === SENSOR.ControllerConnectionHealthy &&
        dataHealth === HealthStatus.Unhealthy
      ) {
        this.throwError("CONTROLLER_CONNECTION_FAILED", ErrorType.Controller);
      } else if (
        (dataSensorId === SENSOR.SensecloudConnectionHealthy ||
          dataSensorId === SENSOR.ControllerConnectionHealthy) &&
        dataHealth === HealthStatus.Healthy
      ) {
        const currentValues = this.store.state.currentSensorData.values;

        // Check if the OTHER sensor is also healthy
        if (
          dataSensorId === SENSOR.SensecloudConnectionHealthy &&
          getHealthStatus(currentValues[SENSOR.ControllerConnectionHealthy]) ===
            HealthStatus.Healthy
        ) {
          this.resetError();
        } else if (
          dataSensorId === SENSOR.ControllerConnectionHealthy &&
          getHealthStatus(currentValues[SENSOR.SensecloudConnectionHealthy]) ===
            HealthStatus.Healthy
        ) {
          this.resetError();
        }
      }

      // Check if the sensor id is in the list of sensors we want to listen to
      if (
        !sensorIds.includes(dataSensorId) &&
        dataSensorId !== SENSOR.SensecloudConnectionHealthy &&
        dataSensorId !== SENSOR.ControllerConnectionHealthy
      )
        return;

      // Overwrite only the updated values
      updateStore(
        {
          lastUpdate: timestamp,
          values: {
            ...this.store.state.currentSensorData.values,
            [dataKey]: dataValue,
          },
          updated: {
            ...this.store.state.currentSensorData.updated,
            [dataKey]: timestamp,
          },
        },
        idKey,
      );
    });

    // Listen to socket server updates (this triggers the onModule updates)
    socket.emit("listen", {});
  }

  private getCurrentDataFromHistorical(
    historicalData: ILiftStatusSensorResponse[],
  ): ILiftStatusSensorDataResponse {
    let lastUpdate: number = 0;
    const values: ILiftStatusValues = {};
    const updated: ILiftStatusUpdated = {};

    historicalData.forEach((sensorRow) => {
      const sensorId = sensorRow.sensor.id;

      // Skip sensors with no data rows
      if (sensorRow.rows.length === 0) return;

      const lastRow = sensorRow.rows[sensorRow.rows.length - 1];
      const timestamp = dayjs(lastRow.ts).valueOf();

      // Track the most recent timestamp across all sensors
      if (timestamp > lastUpdate) {
        lastUpdate = timestamp;
      }

      // Parse value as number if possible, otherwise keep as string
      const numericValue = parseInt(lastRow.val, 10);
      values[sensorId] = isFinite(numericValue) ? numericValue : lastRow.val;
      updated[sensorId] = timestamp;
    });

    return {
      lastUpdate,
      values,
      updated,
    };
  }

  private async throwError(
    errorKey: ErrorMessageKey,
    errorType: TLiftStatusWebComponentErrorType,
    errorDescription?: string,
    additionalInfo?: Socket,
  ) {
    const locale = getLocale(this.store?.state.locale ?? this.language);
    const errorMessage = getErrorMessageForDisplay(errorKey, locale);

    // Log the error for the user
    console.group(
      `%cError%c [LsData] ${errorMessage}`,
      "color: #DC2626; background-color: #FEF2F2; display: inline-block; padding: 0 4px; border-radius: 2px;",
      "",
    );

    if (!isEmpty(errorDescription)) {
      console.log(errorDescription);
    }
    if (!isNil(additionalInfo)) {
      console.log(additionalInfo);
    }

    console.groupEnd();

    if (isNil(this.store)) {
      // We always need a key to identify the store data, so if no key is provided we create one ourselves
      // Normally we use a combination of the objectType and objectId as fallback, but since this is an error we can't do that
      const fallbackKey = this.idKey ?? "error";
      createNewStore(fallbackKey, {
        locale,
      });
      this.store = await getStore(fallbackKey);
    }

    // Update the state with the error
    this.store.set("error", {
      hasError: true,
      messageKey: errorKey,
      type: errorType,
    });
  }

  private async resetError() {
    if (isNil(this.store)) {
      console.warn("[LsData] Cannot reset error: Store is not initialized");
      return false;
    }

    if (this.store.state.error.type === ErrorType.None) return;

    try {
      this.store.set("error", {
        hasError: false,
        messageKey: undefined,
        type: ErrorType.None,
      });

      console.log(
        `%c[LsData]%c ${this.store.t("ui:data.connection_restored")}`,
        "color: #059669; background-color: #ECFDF5; display: inline-block; padding: 0 4px; border-radius: 2px;",
        "",
      );

      return true;
    } catch (error) {
      console.error("[LsData] Failed to reset error:", error);
      return false;
    }
  }

  private closeSocketConnection() {
    this.socket.close();
    this.socket = null;
    this.socketId = null;
  }

  disconnectedCallback() {
    this.closeSocketConnection();

    // Clean up the store when the component is disconnected
    if (this.computedIdKey !== null) {
      removeStore(this.computedIdKey);
      this.computedIdKey = null;
    }
  }
}
